import { Link } from 'react-router'
import { DropdownMenuItem } from '@klear/ui/components/dropdown-menu'
import { DataTableRowActions } from '@klear/ui/components/data-table-row-actions'
import type { Row } from '@tanstack/react-table'
import { DeleteButton } from '@klear/ui/components/delete-button'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { PERMISSION_ACTION_VIEW, PERMISSION_ACTION_EDIT, PERMISSION_ACTION_DELETE } from '@klear/constants'
import noop from 'lodash/noop'
interface BaseModel {
  id?: number
}

interface RowActionsProps<T extends BaseModel> {
  row: Row<T>
  resourceType: string
  appPath: string
  getEditTo?: (id: number) => string
  canEdit?: boolean
  getResourceName: (model: T) => string
  deleteMutation: (params: { id: number }) => Promise<unknown>
  isDeleting?: boolean
  onDelete?: () => void
  isArchived?: boolean
  archiveMutation?: (params: { id: number; isArchived: boolean }) => Promise<unknown>
  isArchiving?: boolean
  onArchive?: () => void
}

export default function RowActions<T extends BaseModel>({
  row,
  appPath,
  getEditTo,
  canEdit = true,
  onDelete = noop,
  resourceType,
  getResourceName,
  deleteMutation,
  isDeleting = false,
  isArchived = false,
  archiveMutation,
  isArchiving = false,
  onArchive = noop
}: RowActionsProps<T>) {
  const { user } = useAuth()
  const id = row.original.id ?? 0
  const editTo = getEditTo ? getEditTo(id) : `${appPath}/${id}?edit=1`

  const handleArchive = () => {
    if (!archiveMutation || isArchiving) return
    void archiveMutation({ id, isArchived: !isArchived }).then(onArchive)
  }

  return (
    <DataTableRowActions>
      {user?.hasPermission(resourceType, PERMISSION_ACTION_VIEW) && (
        <Link to={`${appPath}/${id}`}>
          <DropdownMenuItem>View</DropdownMenuItem>
        </Link>
      )}
      {canEdit && user?.hasPermission(resourceType, PERMISSION_ACTION_EDIT) && (
        <Link to={editTo}>
          <DropdownMenuItem>Edit</DropdownMenuItem>
        </Link>
      )}
      {archiveMutation && user?.hasPermission(resourceType, PERMISSION_ACTION_EDIT) && (
        <DropdownMenuItem disabled={isArchiving} onClick={handleArchive}>
          {isArchived ? 'Restore' : 'Archive'}
        </DropdownMenuItem>
      )}
      {user?.hasPermission(resourceType, PERMISSION_ACTION_DELETE) && (
        <DeleteButton
          wrapperComponent={DropdownMenuItem}
          resourceId={row.original.id ?? 0}
          resourceName={getResourceName(row.original)}
          resourceType={resourceType}
          deleteMutation={deleteMutation}
          isDeleting={isDeleting}
          onSuccess={onDelete}
        />
      )}
    </DataTableRowActions>
  )
}
